import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyPlayerGroupsState } from '@/components/features/player-groups/EmptyPlayerGroupsState';
import { PlayerGroupCardSkeleton } from '@/components/features/player-groups/PlayerGroupCardSkeleton';
import { PlayerGroupFormDialog } from '@/components/features/player-groups/PlayerGroupFormDialog';
import { PlayerGroupCard } from '@/components/shared/cards';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { deletePlayerGroup, restorePlayerGroup } from '@/db/player-groups';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useInitialSync } from '@/hooks/useInitialSync';
import { usePlayerGroups } from '@/hooks/usePlayerGroups';
import { usePlayers } from '@/hooks/usePlayers';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { handleUserFacingError } from '@/lib/error-handler';
import { slideUp, staggerConfig } from '@/lib/motion-config';
import { toastDeleteWithUndo, toastError } from '@/lib/toast-helpers';
import type { PlayerGroup } from '@/schemas';

export const Route = createFileRoute('/player-groups/')({
  component: PlayerGroupsPage,
});

function PlayerGroupsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { isSyncing } = useInitialSync();
  const {
    playerGroups,
    isLoading: isLoadingGroups,
    hasData: hasGroupData,
    refresh: refreshGroups,
  } = usePlayerGroups();
  const { players, refresh: refreshPlayers } = usePlayers();

  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PlayerGroup | undefined>();
  const [groupToDelete, setGroupToDelete] = useState<PlayerGroup | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  useDocumentTitle(`${t('playerGroups.title')} - ${t('app.name', 'One Score')}`);

  const showInitialSkeleton = (isSyncing || isLoadingGroups) && !hasGroupData;

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) {
      return playerGroups.toSorted((a, b) => a.name.localeCompare(b.name));
    }
    const query = searchQuery.toLowerCase().trim();
    return playerGroups
      .filter((group) => {
        const nameMatch = group.name.toLowerCase().includes(query);
        const descriptionMatch = group.description?.toLowerCase().includes(query);
        return nameMatch || descriptionMatch;
      })
      .toSorted((a, b) => a.name.localeCompare(b.name));
  }, [playerGroups, searchQuery]);

  const playerMap = useMemo(() => {
    const map = new Map<string, (typeof players)[number]>();
    for (const player of players) {
      map.set(String(player.id), player);
    }
    return map;
  }, [players]);

  const handleCreate = () => {
    setEditingGroup(undefined);
    setIsFormOpen(true);
  };

  const handleCardClick = (group: PlayerGroup) => {
    void navigate({
      to: '/player-groups/$groupId',
      params: { groupId: String(group.id) },
    });
  };

  const handleDelete = (group: PlayerGroup) => {
    setGroupToDelete(group);
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;
    setIsDeleting(true);
    try {
      await deletePlayerGroup(groupToDelete.id);
      toastDeleteWithUndo(
        t('playerGroups.deleteSuccess'),
        async () => {
          await restorePlayerGroup(groupToDelete.id);
          await refreshGroups();
        },
        t('undo', 'undo'),
      );
      setGroupToDelete(undefined);
      await refreshGroups();
    } catch (error: unknown) {
      const errorMessage = handleUserFacingError(
        error,
        t('playerGroups.deleteError', 'Failed to delete group'),
        { operation: 'deletePlayerGroup', groupId: groupToDelete },
      );
      toastError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    void Promise.all([refreshGroups(), refreshPlayers()]);
  };

  const { shouldAnimate, routeKey } = useRouteAnimation();

  return (
    <div className="w-full max-w-6xl space-y-6 px-4 mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('playerGroups.title')}</h1>
          <p className="text-muted-foreground">{t('playerGroups.description')}</p>
        </div>
        {!showInitialSkeleton && playerGroups.length > 0 && (
          <Button className="w-full sm:w-auto" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t('playerGroups.createButton')}
          </Button>
        )}
      </div>

      {!showInitialSkeleton && playerGroups.length >= 6 && (
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('playerGroups.search')}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {showInitialSkeleton && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <PlayerGroupCardSkeleton key={index} />
          ))}
        </div>
      )}

      {!showInitialSkeleton && playerGroups.length === 0 && (
        <EmptyPlayerGroupsState onCreateClick={handleCreate} />
      )}

      {!showInitialSkeleton && filteredGroups.length > 0 && (
        <motion.div
          key={`player-groups-grid-${routeKey}`}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          variants={slideUp}
          initial={shouldAnimate ? 'initial' : false}
          animate="animate"
          transition={staggerConfig.container}
        >
          <AnimatePresence mode="popLayout">
            {filteredGroups.map((group) => {
              const groupPlayers = group.playerIds
                .map((id) => playerMap.get(String(id)))
                .filter((player): player is (typeof players)[number] => player !== undefined);
              return (
                <motion.div
                  key={group.id}
                  layout
                  layoutId={`player-group-card-${group.id}`}
                  variants={slideUp}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <PlayerGroupCard
                    group={group}
                    players={groupPlayers}
                    onEdit={handleCardClick}
                    onDelete={handleDelete}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {!showInitialSkeleton && playerGroups.length > 0 && filteredGroups.length === 0 && (
        <div className="flex min-h-[200px] items-center justify-center">
          <p className="text-muted-foreground">{t('sessions.noResults')}</p>
        </div>
      )}

      <PlayerGroupFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        players={players}
        group={editingGroup}
        onSuccess={handleFormSuccess}
      />

      <AlertDialog
        open={Boolean(groupToDelete)}
        onOpenChange={(open) => !open && setGroupToDelete(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('playerGroups.deleteConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('playerGroups.deleteConfirm.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('playerGroups.form.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDeleteConfirm()}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:ring-destructive"
            >
              {isDeleting ? t('playerGroups.deleting') : t('playerGroups.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
